import { FUEL_ORDER_STATUS_LABELS, type FuelOrder } from "@station/contracts";
import { Decimal, Money } from "@station/core";
import {
  Alert,
  Button,
  Dialog,
  FormField,
  QuantityText,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Pencil, Send, Truck, Undo2, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { Field, Section } from "../../../../features/expenses/shared.js";
import { FuelOrderForm } from "../../../../features/fuel/order-form.js";
import {
  canEditFuelOrder,
  FUEL_ORDER_STATUS_TONE,
  fuelOrderActions,
  orderTotalLitres,
} from "../../../../features/fuel/order-values.js";
import { useFuelOrder, useFuelPermissions, useRefreshFuel } from "../../../../features/fuel/queries.js";
import { FuelPage, Th } from "../../../../features/fuel/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/akaryakit/siparisler/$id")({ component: FuelOrderDetailPage });

function FuelOrderDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useFuelPermissions();
  const order = useFuelOrder(id);
  const title = order.data?.orderNo ?? t("fuel.orders.detail.title");

  return (
    <FuelPage
      title={title}
      breadcrumb={[
        {
          label: t("menu.fuelOrders"),
          link: (children, className) => (
            <Link to="/akaryakit/siparisler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
    >
      {() =>
        !permissions.canRead ? (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        ) : order.error ? (
          <Alert tone="danger">{errorMessage(order.error)}</Alert>
        ) : !order.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <FuelOrderDetail order={order.data} />
        )
      }
    </FuelPage>
  );
}

function FuelOrderDetail({ order }: { order: FuelOrder }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const permissions = useFuelPermissions();
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const actions = fuelOrderActions(order.status);

  const act = useMutation({
    mutationFn: async (action: "submit" | "reopen" | "mark-sent" | "cancel") => {
      const params = { path: { id: order.id } };
      if (action === "submit")
        return (await api.POST("/fuel/orders/{id}/submit", { params, body: { version: order.version } }))
          .data!;
      if (action === "reopen")
        return (await api.POST("/fuel/orders/{id}/reopen", { params, body: { version: order.version } }))
          .data!;
      if (action === "mark-sent")
        return (await api.POST("/fuel/orders/{id}/mark-sent", { params, body: { version: order.version } }))
          .data!;
      return (
        await api.POST("/fuel/orders/{id}/cancel", {
          params,
          body: { version: order.version, reason },
        })
      ).data!;
    },
    onSuccess: async (_data, action) => {
      setCancelling(false);
      setReason("");
      await refresh();
      toast({ tone: "success", title: t(`fuel.orders.detail.done.${action}`) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  if (editing && canEditFuelOrder(order.status))
    return (
      <FuelOrderForm
        companyId={order.companyId}
        stationId={order.stationId}
        order={order}
        onSaved={() => setEditing(false)}
      />
    );

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{order.orderNo}</span>
        <StatusBadge tone={FUEL_ORDER_STATUS_TONE[order.status]}>
          {FUEL_ORDER_STATUS_LABELS[order.status]}
        </StatusBadge>
        <span className="text-sm text-muted-foreground">{order.distributorName}</span>
        <div className="ms-auto flex flex-wrap gap-2">
          {canEditFuelOrder(order.status) && permissions.canManageOrders ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil aria-hidden="true" />
              {t("common.edit")}
            </Button>
          ) : null}
          {actions.submit && permissions.canManageOrders ? (
            <Button
              loading={act.isPending && act.variables === "submit"}
              onClick={() => act.mutate("submit")}
            >
              <Send aria-hidden="true" />
              {t("fuel.orders.detail.submit")}
            </Button>
          ) : null}
          {actions.reopen && permissions.canManageOrders ? (
            <Button
              variant="outline"
              loading={act.isPending && act.variables === "reopen"}
              onClick={() => act.mutate("reopen")}
            >
              <Undo2 aria-hidden="true" />
              {t("fuel.orders.detail.reopen")}
            </Button>
          ) : null}
          {actions.markSent && permissions.canManageOrders ? (
            <Button
              variant="outline"
              loading={act.isPending && act.variables === "mark-sent"}
              onClick={() => act.mutate("mark-sent")}
            >
              {t("fuel.orders.detail.markSent")}
            </Button>
          ) : null}
          {actions.startDelivery && permissions.canManageDeliveries ? (
            <Button variant="outline" asChild>
              <Link to="/akaryakit/dolumlar/yeni" search={{ orderId: order.id }}>
                <Truck aria-hidden="true" />
                {t("fuel.orders.detail.startDelivery")}
              </Link>
            </Button>
          ) : null}
          {actions.cancel && permissions.canManageOrders ? (
            <Button variant="destructive" onClick={() => setCancelling(true)}>
              <X aria-hidden="true" />
              {t("fuel.orders.detail.cancel")}
            </Button>
          ) : null}
        </div>
      </div>

      {order.cancelReason ? (
        <Alert tone="warning" title={t("fuel.orders.detail.cancelledTitle")}>
          {order.cancelReason}
        </Alert>
      ) : null}

      {/* DBS limit uyarısı BİLGİDİR: siparişi engellemez, sipariş anında belgeye dondurulur. */}
      {order.dbsWarning && order.dbsWarning.exceedsLimit ? (
        <Alert tone="warning" title={t("fuel.orders.dbs.title")}>
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-3">
            <Field label={t("fuel.orders.dbs.limit")}>
              {Money.fromMinor(order.dbsWarning.limitAmount).formatTr()}
            </Field>
            <Field label={t("fuel.orders.dbs.used")}>
              {Money.fromMinor(order.dbsWarning.usedAmount).formatTr()}
            </Field>
            <Field label={t("fuel.orders.dbs.available")}>
              {Money.fromMinor(order.dbsWarning.availableAmount).formatTr()}
            </Field>
          </dl>
          <p>
            {t("fuel.orders.dbs.body", {
              estimated:
                order.estimatedAmount === null ? "—" : Money.fromMinor(order.estimatedAmount).formatTr(),
              checkedAt: formatDateTime(order.dbsWarning.checkedAt),
            })}
          </p>
        </Alert>
      ) : null}

      <Section title={t("fuel.orders.detail.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("fuel.orders.fields.distributor")}>{order.distributorName}</Field>
          <Field label={t("fuel.orders.fields.requestedDeliveryDate")}>
            {formatDate(order.requestedDeliveryDate)}
          </Field>
          <Field label={t("fuel.orders.fields.totalL")}>
            {`${orderTotalLitres(order.lines).formatTr(3)} L`}
          </Field>
          <Field label={t("fuel.orders.fields.estimatedAmount")}>
            {order.estimatedAmount === null ? "—" : Money.fromMinor(order.estimatedAmount).formatTr()}
          </Field>
          <Field label={t("fuel.orders.fields.sentAt")}>{formatDateTime(order.sentAt)}</Field>
          <Field label={t("fuel.orders.fields.createdAt")}>{formatDateTime(order.createdAt)}</Field>
        </dl>
        {order.note ? <p className="text-sm">{order.note}</p> : null}

        <div
          className="overflow-x-auto rounded-lg border"
          tabIndex={0}
          role="group"
          aria-label={t("fuel.orders.detail.linesLabel")}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">{t("fuel.orders.detail.linesLabel")}</caption>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("fuel.orders.fields.product")}</Th>
                <Th align="right">{t("fuel.orders.fields.quantityL")}</Th>
                <Th align="right">{t("fuel.orders.fields.deliveredL")}</Th>
                <Th align="right">{t("fuel.orders.fields.remainingL")}</Th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <tr key={line.id} className="border-t">
                  <td className="px-3 py-2">{line.productName}</td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={line.quantityL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={line.deliveredL} unit="LITRE" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText
                      value={Decimal.from(line.quantityL).sub(line.deliveredL).toString()}
                      unit="LITRE"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {order.approvalRequestId ? (
        <Section title={t("fuel.orders.detail.approval")}>
          <ApprovalRequestPanel requestId={order.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("fuel.orders.detail.cancelTitle")}
        description={t("fuel.orders.detail.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("fuel.orders.detail.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" loading={act.isPending}>
              {t("fuel.orders.detail.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
